import SectionHeading from "components/SectionHeading";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import mixpanel from "mixpanel-browser";

import { auth } from "_firebase";

import { actions as userActions } from "store/users";
import { checkValidity } from "utils/fields";
import { updateObject } from "utils";
import { changeUserPassword } from "utils/auth/api";
import { maxLength } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";
import {
    getEffectiveEmailConfirmed,
    getEffectiveUserVerified,
    isRegisteredEnergyAdvisor,
} from "utils/user/verification";
import { TOUR_CONFIGS, isTourShown, resetTourSessionState } from "utils/tours";

import Input from "components/Input";
import Toggle from "components/Input/Toggle";
import Button from "components/Button";

import classes from "./style.module.scss";

const { saveUserMeta } = userActions;

const charLim200 = maxLength(200);

const initPassFormState = {
    oldPassword: {
        name: "oldPassword",
        value: "",
        type: "password",
        label: "Current Password",
        placeholder: "Your Current Password",
        valid: false,
        validation: {
            required: true,
            minLength: 6,
        },
        validMessage: "",
        touched: false,
    },
    newPassword: {
        name: "newPassword",
        value: "",
        type: "password",
        label: "New Password",
        placeholder: "Your New Password",
        valid: false,
        validation: {
            required: true,
            minLength: 10,
            containLetter: true,
            containNumber: true,
            containSpecialChar: true,
        },
        validMessage: "",
        touched: false,
    },
    confirmNewPassword: {
        name: "confirmNewPassword",
        value: "",
        type: "password",
        label: "Confirm New Password",
        placeholder: "Confirm Your New Password",
        valid: false,
        validation: {
            required: true,
            minLength: 10,
            containLetter: true,
            containNumber: true,
            containSpecialChar: true,
        },
        validMessage: "",
        touched: false,
    },
};

const StatusNotice = ({ label, value, tone = "neutral", detail }) => (
    <div className={classes.statusNotice}>
        <div className={classes.statusHeader}>
            <span className={classes.statusLabel}>{label}</span>
            <span
                className={
                    tone === "positive"
                        ? classes.statusPositive
                        : tone === "negative"
                          ? classes.statusNegative
                          : classes.statusNeutral
                }
            >
                {value}
            </span>
        </div>
        {detail && <p className={classes.statusDetail}>{detail}</p>}
    </div>
);

export default () => {
    const dispatch = useDispatch();
    const [valid, setValid] = useState(false);
    const [error, setError] = useState("");
    const [newPasswordMatch, setNewPasswordMatch] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitBtnLabel, setSubmitBtnLabel] = useState("Change Password");
    const [passForm, setPassForm] = useState(initPassFormState);

    const {
        uid,
        createdAt = null,
        isUserVerified = false,
        isEmailConfirmed = false,
        nonErsUser = true,
        userMeta = {},
    } = useStateSelector("user") || {};

    const accountVerified = getEffectiveUserVerified({ createdAt, isUserVerified });
    const emailConfirmed = getEffectiveEmailConfirmed({ createdAt, isEmailConfirmed });
    const isRea = isRegisteredEnergyAdvisor({ nonErsUser });

    const handleTourToggle = useCallback(
        (tourId, showAgain) => {
            const tourConfig = TOUR_CONFIGS[tourId];
            if (!tourConfig || !uid) return;

            if (showAgain) {
                resetTourSessionState(tourId);
            }

            dispatch(
                saveUserMeta({
                    uid,
                    userMeta: { [tourConfig.key]: !showAgain },
                })
            );
        },
        [dispatch, uid]
    );

    const onChangeHandler = (value, fieldName) => {
        const { isValid, message } = checkValidity(passForm[fieldName].label, value, passForm[fieldName].validation);

        const updatedControls = updateObject(passForm, {
            [fieldName]: updateObject(passForm[fieldName], {
                value,
                valid: isValid,
                validMessage: message,
                touched: true,
            }),
        });

        setValid(Object.values(updatedControls).every(({ valid }) => valid));
        setPassForm(updatedControls);
    };

    useEffect(() => {
        if (
            passForm.newPassword.value === passForm.confirmNewPassword.value &&
            passForm.confirmNewPassword.value !== "" &&
            passForm.newPassword.value !== ""
        ) {
            setNewPasswordMatch(true);
        } else {
            setNewPasswordMatch(false);
        }
        setSubmitBtnLabel("Change Password");

        if (
            passForm.newPassword.value !== passForm.confirmNewPassword.value &&
            passForm.confirmNewPassword.value !== "" &&
            passForm.newPassword.value !== ""
        ) {
            setSubmitBtnLabel("New Passwords Must Match");
        }
    }, [passForm]);

    const handlePasswordSubmit = async () => {
        // handleSubmit()
        setSubmitting(true);
        setSubmitBtnLabel("Submitting...");
        const response = await changeUserPassword({
            oldPassword: passForm.oldPassword.value,
            newPassword: passForm.newPassword.value,
        });

        if (isEmpty(response.err)) {
            setSubmitting(false);
            setPassForm(initPassFormState);
            setError("");
            setSubmitBtnLabel("Password Changed!");
            setTimeout(() => {
                setSubmitBtnLabel("Logging out...");
            }, 1000);
            setTimeout(() => {
                mixpanel.track("Logout");
                mixpanel.reset();
                auth.signOut();
            }, 2000);
        } else {
            const { message: statusMessage = "Request failed with status code 500" } = response.err.toJSON();
            let errorCode = response?.err?.response?.data?.error || statusMessage;

            if (errorCode === "auth/wrong-password") {
                errorCode = "The old password provided is invalid.";
            }

            setError(errorCode);
            setSubmitBtnLabel("Change Password");
        }
    };

    return (
        <div>
            <SectionHeading title="User Account" subtitle="View your account status and change your password" />

            <div className={classes.contentGrid}>
                <div className={classes.statusWrapper}>
                    <h3>Account Status</h3>
                    <p className={classes.statusIntro}>
                        These details are managed by Volta Research and cannot be edited.
                    </p>

                    <StatusNotice
                        label="Account verification"
                        value={accountVerified ? "Verified" : "Unverified"}
                        tone={accountVerified ? "positive" : "negative"}
                        detail={
                            accountVerified
                                ? "Your account has full access to verified-user features."
                                : "Unverified accounts are limited to five models and cannot access Insights, Parametric Analysis, non-General simulations, or F280 reports. Contact Volta Research to request verification."
                        }
                    />
                    <StatusNotice
                        label="Email confirmation"
                        value={emailConfirmed ? "Confirmed" : "Not confirmed"}
                        tone={emailConfirmed ? "positive" : "negative"}
                        detail={
                            emailConfirmed
                                ? "Your email address has been confirmed."
                                : "Please confirm your email address using the link sent to your inbox."
                        }
                    />
                    <StatusNotice
                        label="Registered Energy Advisor"
                        value={isRea ? "Yes" : "No"}
                        tone={isRea ? "positive" : "neutral"}
                        detail={
                            isRea
                                ? "Your account can run simulations in EnerGuide and Ontario Reference House modes."
                                : "ERS and ORH simulation modes require separate verification as a Registered Energy Advisor in good standing with NRCan."
                        }
                    />
                </div>

                <div className={classes.authWrapper}>
                    <form className={classes.formWrapper}>
                        <h3>Change Password</h3>
                        {Object.values(passForm).map(
                            ({ name, type, label, valid, validMessage, touched, placeholder, value }) => (
                                <Input
                                    key={name}
                                    large
                                    name={name}
                                    type={type}
                                    label={label}
                                    placeholder={placeholder}
                                    // hideLabel
                                    invalid={!valid}
                                    validMessage={validMessage}
                                    touched={touched}
                                    className={classes.input}
                                    input={{
                                        onChange: (value) => onChangeHandler(value, name),
                                        value: value,
                                    }}
                                />
                            )
                        )}
                        <Button
                            disabled={!valid || !newPasswordMatch || submitting}
                            type="submit"
                            onClick={(event) => {
                                setSubmitting(true);
                                handlePasswordSubmit();
                            }}
                        >
                            {submitBtnLabel}
                        </Button>
                        <div className={classes.errorMsg}>{error && <p>{error}</p>}</div>
                        <p>
                            Your new password must be at least 10 characters long and contain at least one letter,
                            number, and special character (e.g. !, $, &).
                        </p>
                        <p>You will be logged out after successfully changing your password.</p>
                    </form>
                </div>
            </div>

            <div className={classes.creditsWrapper}>
                <h3>Credits</h3>
                <p className={classes.creditsIntro}>
                    View your credit purchases, transfers, and usage.
                </p>
                <Link to="/user/transactions" className={classes.transactionsLink}>
                    View transaction history
                </Link>
            </div>

            <div className={classes.toursWrapper}>
                <h3>Guided Tours</h3>
                <p className={classes.toursIntro}>
                    Turn on a tour to see it again the next time you visit that page.
                </p>
                <div className={classes.tourList}>
                    {Object.entries(TOUR_CONFIGS).map(([tourId, { key, label }]) => {
                        const showAgain = !isTourShown(userMeta[key]);
                        return (
                            <div key={tourId} className={classes.tourRow}>
                                <span className={classes.tourLabel}>{label}</span>
                                <Toggle
                                    active={showAgain}
                                    onChange={() => handleTourToggle(tourId, !showAgain)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
